// No 'use client': Button and Tab call these while rendering in server
// components, where a client module's exports can't be called.
import type { ReactNode } from 'react'

import { useId } from 'react'

/** A falsy-rendering reason (`cond && 'why'`) leaves the element enabled. */
export function hasDisabledReason(reason: ReactNode): boolean {
  return (
    reason !== undefined && reason !== null && reason !== false && reason !== ''
  )
}

interface DisabledReasonDescription {
  /** Merged with the caller's own `aria-describedby`. */
  describedBy: string | undefined
  /** Render inside the disabled element; `hidden` keeps it out of the name. */
  description: ReactNode
}

/**
 * Exposes the reason to assistive tech: Base UI tooltips set no
 * `aria-describedby`, so the popup alone is visual-only.
 */
export function useDisabledReasonDescription(
  reason: ReactNode,
  describedBy: string | undefined,
): DisabledReasonDescription {
  const id = useId()
  if (!hasDisabledReason(reason)) {
    return { describedBy, description: null }
  }
  const mergedDescribedBy = describedBy ? `${describedBy} ${id}` : id
  const description = (
    <span hidden id={id}>
      {reason}
    </span>
  )
  return { describedBy: mergedDescribedBy, description }
}
